import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Define interfaces for TagDefinition and API responses
export interface TagDefinition {
  id: string;
  key: string;
  description: string | null;
  organizationId: string;
  divisionId: string | null;
  valueType: string;
  cardinality: string;
  isRequired: boolean;
  isBuiltIn: boolean;
  allowDivisionValues: boolean;
  validFor: string[];
  metadataSchema: Record<string, unknown> | null;
  color: string | null;
  icon: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface TagDefinitionListResponse {
  tagDefinitions: TagDefinition[];
}

export interface CreateTagDefinitionRequest {
  key: string;
  description?: string | null;
  divisionId?: string | null;
  valueType: string;
  cardinality?: string | null;
  isRequired?: boolean;
  allowDivisionValues?: boolean;
  validFor: string[];
  metadataSchema?: Record<string, unknown> | null;
  color?: string | null;
  icon?: string | null;
}

export interface UpdateTagDefinitionRequest {
  description?: string | null;
  cardinality?: string | null;
  isRequired?: boolean;
  allowDivisionValues?: boolean;
  validFor?: string[] | null;
  metadataSchema?: Record<string, unknown> | null;
  color?: string | null;
  icon?: string | null;
}

interface TagDefinitionResponse {
  id: string;
  key: string;
  description: string | null;
  organizationId: string;
  divisionId: string | null;
  valueType: string;
  cardinality: string;
  isRequired: boolean;
  isBuiltIn: boolean;
  allowDivisionValues: boolean;
  validFor: string[];
  metadataSchema: Record<string, unknown> | null;
  color: string | null;
  icon: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiErrorResponse {
  error: string;
  description?: string;
}

const backendApi = config.backendApi;

// Fetch all tag definitions
const fetchTagDefinitions = async (): Promise<TagDefinition[]> => {
  const response = await axios.get<TagDefinitionListResponse>(
    `${backendApi}/api/tag-definitions`,
    { withCredentials: true }
  );
  return response.data.tagDefinitions;
};

// Fetch tag definitions for a specific resource type
const fetchTagDefinitionsForResourceType = async (resourceType: string): Promise<TagDefinition[]> => {
  const response = await axios.get<TagDefinitionListResponse>(
    `${backendApi}/api/tag-definitions/for-resource-type/${resourceType}`,
    { withCredentials: true }
  );
  return response.data.tagDefinitions;
};

// Create a new tag definition
const createTagDefinition = async (data: CreateTagDefinitionRequest): Promise<TagDefinitionResponse> => {
  const response = await axios.post<TagDefinitionResponse>(
    `${backendApi}/api/tag-definitions`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

// Update an existing tag definition
const updateTagDefinition = async (id: string, data: UpdateTagDefinitionRequest): Promise<TagDefinitionResponse> => {
  const response = await axios.patch<TagDefinitionResponse>(
    `${backendApi}/api/tag-definitions/${id}`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

// Delete a tag definition
const deleteTagDefinition = async (id: string): Promise<void> => {
  await axios.delete(`${backendApi}/api/tag-definitions/${id}`, { withCredentials: true });
};

// Hook to fetch all tag definitions
export function useTagDefinitions(organizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<TagDefinition[], AxiosError<ApiErrorResponse>>({
    queryKey: ['tagDefinitions', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch tag definitions.");
      }
      return fetchTagDefinitions();
    },
    enabled: !!organizationId && contextReady,
  });
}

// Hook to fetch tag definitions for a specific resource type
export function useTagDefinitionsForResourceType(
  organizationId: string | null | undefined,
  resourceType: string | null | undefined
) {
  const contextReady = useActiveContextReady();

  return useQuery<TagDefinition[], AxiosError<ApiErrorResponse>>({
    queryKey: ['tagDefinitions', organizationId, 'resourceType', resourceType],
    queryFn: () => {
      if (!organizationId || !resourceType) {
        throw new Error("organizationId and resourceType are required to fetch tag definitions.");
      }
      return fetchTagDefinitionsForResourceType(resourceType);
    },
    enabled: !!organizationId && !!resourceType && contextReady,
  });
}

// Hook to create a tag definition
export function useCreateTagDefinition() {
  const queryClient = useQueryClient();

  return useMutation<TagDefinitionResponse, AxiosError<ApiErrorResponse>, CreateTagDefinitionRequest>({
    mutationFn: createTagDefinition,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tagDefinitions'] });
    },
  });
}

// Hook to update a tag definition
export function useUpdateTagDefinition() {
  const queryClient = useQueryClient();

  return useMutation<TagDefinitionResponse, AxiosError<ApiErrorResponse>, { id: string; data: UpdateTagDefinitionRequest }>({
    mutationFn: ({ id, data }) => updateTagDefinition(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tagDefinitions'] });
    },
  });
}

// Hook to delete a tag definition
export function useDeleteTagDefinition() {
  const queryClient = useQueryClient();

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  return useMutation<void, AxiosError<ApiErrorResponse>, string>({
    mutationFn: deleteTagDefinition,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tagDefinitions'] });
    },
  });
}
